import { isEqual } from 'lodash';
import { useCallback, useEffect, useRef, useState } from 'react';
import submissionService from 'services/submission';

// sets the delay before an ordinary draft save
const AUTOSAVE_DELAY = 800;

// creates the persistence state owned by one submission
const createSubmissionState = (persistedPayload) => ({
    activeUpdates: 0,
    error: null,
    latestUpdate: null,
    pendingDraft: null,
    persistedPayload,
    queue: Promise.resolve(),
});

// checks whether a draft still needs to enter its queue
const shouldQueueDraft = (state, payload) => {
    return payload && !isEqual(payload, state.persistedPayload) && !isEqual(payload, state.pendingDraft?.payload);
};

// manages debounced and explicit grading submission updates
const useGradingDraftAutosave = ({ enabled, onError, onSaved, payload, persistedPayload, submissionId }) => {
    // stores queue and draft state separately for each submission
    const submissionStatesRef = useRef(new Map());
    // stores the active debounce timer
    const timerRef = useRef(null);
    // exposes the selected submission save error
    const [draftSaveError, setDraftSaveError] = useState(null);
    // exposes whether the selected submission has queued updates
    const [isSavingDraft, setIsSavingDraft] = useState(false);
    // keeps the latest hook values available to queued work
    const latestRef = useRef({ enabled, onError, onSaved, payload, persistedPayload, submissionId });
    // prevents queued work from updating state after unmount
    const isMountedRef = useRef(true);
    // tracks whether automatic draft scheduling is paused
    const isPausedRef = useRef(false);

    latestRef.current = { enabled, onError, onSaved, payload, persistedPayload, submissionId };

    // returns or creates the persistence state for a submission
    const getSubmissionState = useCallback((targetSubmissionId, initialPayload) => {
        if (!submissionStatesRef.current.has(targetSubmissionId)) {
            submissionStatesRef.current.set(targetSubmissionId, createSubmissionState(initialPayload));
        }
        return submissionStatesRef.current.get(targetSubmissionId);
    }, []);

    // clears the active debounce timer
    const clearTimer = useCallback(() => {
        if (timerRef.current === null) return;
        clearTimeout(timerRef.current);
        timerRef.current = null;
    }, []);

    // syncs public state with the selected submission
    const syncSelectedState = useCallback(() => {
        if (!isMountedRef.current) return;
        const state = submissionStatesRef.current.get(latestRef.current.submissionId);
        setDraftSaveError(state?.error ?? null);
        setIsSavingDraft(Boolean(state?.activeUpdates));
    }, []);

    // serializes an update within its submission queue
    const enqueueSubmissionUpdate = useCallback(
        ({
            submissionId: targetSubmissionId,
            payload: updatePayload,
            draftPayload,
            applyResponse = true,
            notifyOnError = true,
        }) => {
            if (!targetSubmissionId) return Promise.resolve(null);

            const state = getSubmissionState(targetSubmissionId);
            const updateToken = {};
            state.activeUpdates += 1;
            state.latestUpdate = updateToken;
            syncSelectedState();

            const operation = state.queue.then(async () => {
                try {
                    const savedSubmission = await submissionService.updateSubmission(targetSubmissionId, updatePayload);
                    if (draftPayload !== undefined) state.persistedPayload = draftPayload;
                    if (state.latestUpdate === updateToken) {
                        state.error = null;
                        if (applyResponse) latestRef.current.onSaved?.(savedSubmission, targetSubmissionId);
                    }
                    return savedSubmission;
                } catch (error) {
                    if (state.latestUpdate === updateToken) {
                        state.error = error;
                        if (notifyOnError) latestRef.current.onError?.(error);
                    }
                    throw error;
                }
            });

            state.queue = operation.catch(() => undefined);
            if (draftPayload !== undefined) state.pendingDraft = { operation, payload: draftPayload };

            // completes local tracking for this update
            const finishOperation = () => {
                state.activeUpdates -= 1;
                if (state.pendingDraft?.operation === operation) state.pendingDraft = null;
                syncSelectedState();
            };
            operation.then(finishOperation, finishOperation);

            return operation;
        },
        [getSubmissionState, syncSelectedState]
    );

    // schedules the latest unsaved draft
    const scheduleDraftSave = useCallback(() => {
        clearTimer();
        const { enabled: isEnabled, payload: latestPayload, submissionId: targetSubmissionId } = latestRef.current;
        if (!isEnabled || isPausedRef.current || !targetSubmissionId || !latestPayload) return;

        const state = getSubmissionState(targetSubmissionId, latestRef.current.persistedPayload);
        if (!shouldQueueDraft(state, latestPayload)) return;

        timerRef.current = setTimeout(() => {
            timerRef.current = null;
            const current = latestRef.current;
            if (!current.enabled || isPausedRef.current || current.submissionId !== targetSubmissionId) return;

            const currentState = getSubmissionState(targetSubmissionId, current.persistedPayload);
            if (!shouldQueueDraft(currentState, current.payload)) return;

            enqueueSubmissionUpdate({
                submissionId: targetSubmissionId,
                payload: current.payload,
                draftPayload: current.payload,
            }).catch(() => undefined);
        }, AUTOSAVE_DELAY);
    }, [clearTimer, enqueueSubmissionUpdate, getSubmissionState]);

    // cancels a draft that has not entered the queue
    const cancelScheduledSave = clearTimer;

    // waits until the selected submission has persisted its latest draft
    const flushDraftSave = useCallback(async () => {
        clearTimer();
        const targetSubmissionId = latestRef.current.submissionId;
        if (!latestRef.current.enabled || !targetSubmissionId) return;

        while (latestRef.current.submissionId === targetSubmissionId) {
            const latestPayload = latestRef.current.payload;
            const state = getSubmissionState(targetSubmissionId, latestRef.current.persistedPayload);
            if (!latestPayload || isEqual(latestPayload, state.persistedPayload)) return;

            if (isEqual(latestPayload, state.pendingDraft?.payload)) {
                await state.pendingDraft.operation;
            } else {
                await enqueueSubmissionUpdate({
                    submissionId: targetSubmissionId,
                    payload: latestPayload,
                    draftPayload: latestPayload,
                });
            }
        }
    }, [clearTimer, enqueueSubmissionUpdate, getSubmissionState]);

    // pauses automatic draft scheduling
    const pauseAutosave = useCallback(() => {
        isPausedRef.current = true;
        clearTimer();
    }, [clearTimer]);

    // replaces the selected submission persisted baseline
    const resetDraftSave = useCallback(
        (nextPayload) => {
            clearTimer();
            latestRef.current.payload = nextPayload;
            const state = getSubmissionState(latestRef.current.submissionId, nextPayload);
            state.persistedPayload = nextPayload;
            state.error = null;
            syncSelectedState();
        },
        [clearTimer, getSubmissionState, syncSelectedState]
    );

    // resumes automatic draft scheduling
    const resumeAutosave = useCallback(() => {
        isPausedRef.current = false;
        scheduleDraftSave();
    }, [scheduleDraftSave]);

    // selects the persistence state for the current submission
    useEffect(() => {
        clearTimer();
        if (submissionId) getSubmissionState(submissionId, latestRef.current.persistedPayload);
        isPausedRef.current = false;
        syncSelectedState();
    }, [clearTimer, enabled, getSubmissionState, submissionId, syncSelectedState]);

    // restarts the debounce when the selected draft changes
    useEffect(() => {
        scheduleDraftSave();
        return clearTimer;
    }, [clearTimer, enabled, payload, scheduleDraftSave, submissionId]);

    // stops local updates while allowing queued requests to finish
    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
            clearTimer();
        };
    }, [clearTimer]);

    return {
        cancelScheduledSave,
        draftSaveError,
        enqueueSubmissionUpdate,
        flushDraftSave,
        isSavingDraft,
        pauseAutosave,
        resetDraftSave,
        resumeAutosave,
    };
};

export default useGradingDraftAutosave;
